import { differenceInDays } from 'date-fns';
import { FraudReport, FraudReportStatusInfo } from 'fraud/types';

const DAYS_BEFORE_IP_IS_STALE = 30;

/**
 * Helper to check if a report's IP address is stale based on generation date
 */
const isIpAddressStale = (
  fraudReport: FraudReport | null | undefined
): boolean => {
  if (!fraudReport || fraudReport.status !== 'success') {
    return false;
  }

  const { digital_profile, generated_at } = fraudReport;

  if (!digital_profile?.ip_address || !generated_at) {
    return false;
  }

  const daysSinceGeneration = Math.abs(
    differenceInDays(new Date(generated_at), new Date())
  );

  return daysSinceGeneration >= DAYS_BEFORE_IP_IS_STALE;
};

/**
 * Helper to check if a report has missing data (partial check)
 */
const isMissingData = (
  fraudReport: FraudReport | null | undefined
): boolean => {
  if (!fraudReport || fraudReport.status !== 'success') {
    return false;
  }

  const missingData = fraudReport?.digital_profile?.missing_data;
  return !!(missingData && missingData.length > 0);
};

export const useFraudReportStatus = (
  fraudReport: FraudReport | null | undefined,
  isActiveApplication = true
): FraudReportStatusInfo => {
  const reportStatus = fraudReport && fraudReport.status;

  const isRunnableInsufficientDataReport =
    reportStatus === 'insufficient_data' &&
    !!fraudReport?.required_data_present;
  const isReportEmpty = !reportStatus || isRunnableInsufficientDataReport;
  const isReportFailed = reportStatus === 'failed';
  const isReportInsufficientData =
    reportStatus === 'insufficient_data' && !isRunnableInsufficientDataReport;
  const isReportPending = reportStatus === 'pending';
  const isReportSuccess = reportStatus === 'success';
  const isReportStale = isIpAddressStale(fraudReport);
  const isReportPartiallyChecked = isMissingData(fraudReport);
  const isReportResolved = isReportSuccess && fraudReport?.resolved_at !== null;

  const getDisplayStatus = () => {
    if (isReportEmpty && !isActiveApplication) {
      return 'inactive';
    }
    if (isRunnableInsufficientDataReport) {
      return 'ready_to_run';
    }
    if (isReportEmpty) {
      return 'empty';
    }
    if (isReportPartiallyChecked) {
      return 'partial_check';
    }
    if (isReportStale) {
      return 'stale_ip';
    }
    return reportStatus;
  };

  return {
    reportStatus,
    displayStatus: getDisplayStatus(),
    isReportEmpty,
    isReportFailed,
    isReportInsufficientData,
    isReportPending,
    isReportStale,
    isReportSuccess,
    isReportPartiallyChecked,
    isReportResolved,
  };
};
